/**
 * Festive decoration — the images and the one greeting the seasonal theme adds
 * on top of the existing pages (Diwali 2026 first). Pure presentation: every
 * piece renders null unless the `seasonal` flag object says `active`, and
 * every image is absolutely positioned (classes in `festive.css`) so no page
 * layout moves. The host element needs `pb-festive-host` (relative + isolate)
 * so the mandala can sit behind its content at z-index -1.
 *
 * Pieces:
 *   <FestiveLanterns>  — hanging lanterns, Home only, shown with the greeting
 *   <FestiveMandala>   — faint gold mandala wash behind a page's first screen
 *   <FestiveMarigolds> — marigold cluster, right end of the last quick-link row
 *                        (Home) or the bottom-right corner (order placed)
 *   <FestiveGreeting>  — "Shubh Deepavali" + one line, greeting window only
 */
import { Sparkle } from 'lucide-react';
import { SEASONAL_ASSETS } from '../utils/seasonalTheme';

function assets(seasonal) {
  return SEASONAL_ASSETS[seasonal?.key] || SEASONAL_ASSETS.diwali;
}

/** Hanging lanterns at the host's top-right. Home only; follows the greeting window. */
export function FestiveLanterns({ seasonal }) {
  if (!seasonal?.active || !seasonal?.greeting_active) return null;
  return <img src={assets(seasonal).lanterns} alt="" aria-hidden="true" className="pb-festive-img pb-festive-lanterns" data-testid="festive-lanterns" />;
}

/** Faint mandala behind the host's content. `page` nudges it below a sticky header. */
export function FestiveMandala({ seasonal, page = false }) {
  if (!seasonal?.active) return null;
  return (
    <img
      src={assets(seasonal).mandala}
      alt=""
      aria-hidden="true"
      className={`pb-festive-img pb-festive-mandala${page ? ' pb-festive-mandala--page' : ''}`}
      data-testid="festive-mandala"
    />
  );
}

/** Marigold cluster. `corner` pins it to the host's bottom-right (order placed); `snug` is the smaller size for a four-circle row. */
export function FestiveMarigolds({ seasonal, corner = false, snug = false }) {
  if (!seasonal?.active) return null;
  return (
    <img
      src={assets(seasonal).marigolds}
      alt=""
      aria-hidden="true"
      className={`pb-festive-img pb-festive-marigolds${corner ? ' pb-festive-marigolds--corner' : ''}${snug ? ' pb-festive-marigolds--snug' : ''}`}
      data-testid="festive-marigolds"
    />
  );
}

/** Two-line greeting above the first quick-link row; copy comes from admin. Greeting window only. */
export function FestiveGreeting({ seasonal }) {
  if (!seasonal?.active || !seasonal?.greeting_active) return null;
  return (
    <div className="pb-festive-greeting" data-testid="festive-greeting">
      <p className="pb-festive-greeting__title font-fraunces tracking-tight">
        {seasonal.greeting_title || 'Shubh Deepavali'}
        <Sparkle className="w-3.5 h-3.5" fill="currentColor" strokeWidth={1.5} aria-hidden="true" />
      </p>
      {seasonal.greeting_subline ? <p className="pb-festive-greeting__line">{seasonal.greeting_subline}</p> : null}
    </div>
  );
}
